import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

export default function Contact() {
  return (
    <section id="contact" className="relative py-24 min-h-[calc(100vh-200px)] flex flex-col justify-center border-t border-white/5 bg-navy-deep/20">

      {/* Glow highlight */}
      <div className="absolute top-[40%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-brand-cyan/2 blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-8 w-full">

        {/* Title */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan text-xs font-bold uppercase tracking-widest mb-6"
          >
            <span>Let's Connect</span>
          </motion.div>

          <h2 className="font-display font-black text-4xl md:text-5xl text-white tracking-tight mb-4">
            Connect with <span className="text-gradient">Our Experts</span>
          </h2>

          <p className="font-sans text-slate-400">
            Partner with us to secure, scale, and optimize your business technology infrastructure.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

          {/* Left Column: Office Details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8 flex"
          >
            <div className="w-full rounded-3xl glass-panel p-8 md:p-10 shadow-2xl flex flex-col justify-between h-full relative overflow-hidden border border-white/5 bg-navy-dark/40 backdrop-blur-md">
              {/* Glow background effect inside card */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-cyan/5 rounded-full blur-3xl pointer-events-none" />

              <div className="w-full">
                <h3 className="font-display font-bold text-2xl text-white mb-4 text-left">
                  Advance IT Services Head Office
                </h3>

                <p className="font-sans text-sm text-slate-400 leading-relaxed mb-10 text-left">
                  Serving corporate entities, factories, and office ecosystems since 1999. Reach out to schedule an on-site audit, discuss hardware leasing pipelines, or consult with our systems architects.
                </p>

                {/* Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                  {/* Address */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-brand-cyan shrink-0">
                      <MapPin size={20} />
                    </div>
                    <div className="text-left">
                      <h4 className="font-display font-bold text-xs text-slate-500 uppercase tracking-wider">Office Location</h4>
                      <p className="font-sans text-xs md:text-sm text-slate-300 leading-relaxed mt-1.5">
                        4th Floor, 401- 402, Meghansh Complex, Gurukul Rd, opposite Oxford Tower, Memnagar <br />
                        Ahmedabad, Gujarat 380052
                      </p>
                    </div>
                  </div>

                  {/* Call */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-brand-cyan shrink-0">
                      <Phone size={20} />
                    </div>
                    <div className="text-left">
                      <h4 className="font-display font-bold text-xs text-slate-500 uppercase tracking-wider">Call Channels</h4>
                      <a href="tel:+919824464400" className="font-sans text-xs md:text-sm text-slate-300 hover:text-brand-cyan transition-colors block mt-1.5 font-semibold">
                        +91 98244 64400
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-brand-cyan shrink-0">
                      <Mail size={20} />
                    </div>
                    <div className="text-left">
                      <h4 className="font-display font-bold text-xs text-slate-500 uppercase tracking-wider">Email Channels</h4>
                      <a href="mailto:info@advanceitservices.in" className="font-sans text-xs md:text-sm text-slate-300 hover:text-brand-cyan transition-colors block mt-1.5 font-semibold">
                        info@advanceitservices.in
                      </a>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-brand-cyan shrink-0">
                      <Clock size={20} />
                    </div>
                    <div className="text-left">
                      <h4 className="font-display font-bold text-xs text-slate-500 uppercase tracking-wider">Business Hours</h4>
                      <p className="font-sans text-xs md:text-sm text-slate-300 mt-1.5">
                        Mon - Sat: 9:30 AM - 7:00 PM <br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="mt-12 pt-6 border-t border-white/5 flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
                <span className="text-[10px] font-mono text-slate-500 tracking-wider uppercase">Operating Ahmedabad NOC Core</span>
              </div> */}
            </div>
          </motion.div>

          {/* Right Column: Google Maps */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4 flex"
          >
            <div className="w-full rounded-3xl overflow-hidden border border-white/5 relative shadow-2xl min-h-[400px] lg:min-h-0 h-full flex">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d229.45959931264386!2d72.53366091716681!3d23.04749493681244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e849daaaaaaab%3A0x86d2f67544b3847c!2sAdvance%20Infotech%20Services!5e0!3m2!1sen!2sin!4v1723706378028!5m2!1sen!2sin" width="600"
                height="450"
                style={{ border: "0" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              {/* Overlay to catch random scroll behaviors on maps */}
              <div className="absolute inset-0 pointer-events-none border border-white/5 rounded-3xl" />
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}
